import React, {useEffect, useRef, useState, useCallback} from 'react';
import {useRouter} from 'next/router';
import {getLocaleContent} from '@helper/locale-content.js';
import {useNewsStore} from '@stores/useNewsStore';
import {useMasterDataStore} from '@stores/useMasterDataStore';
import NewsService from '@services/news.service';
import FilterInput from '@components/common/FilterInput';
import FilterSelect from '@components/common/FilterSelect';
import FilterButton from '@components/common/FilterButton';
import LoadingNavigation from '@components/common/Loading/LoadingNavigation';
import LinkPromotionDetail from '@components/common/Link/LinkPromotionDetail';

export default function NewsListPromotion() {
    const router = useRouter();
    const {locale} = router;

    const searchTimeoutRef = useRef(null);
    const news = useNewsStore(s => s.news);
    const pageIndex = useNewsStore(s => s.pageIndex);
    const hasMore = useNewsStore(s => s.hasMore);
    const totalItems = useNewsStore(s => s.totalItems);
    const isLoading = useNewsStore(s => s.isLoading);
    const filters = useNewsStore(s => s.filters);
    const setFiltersStore = useNewsStore(s => s.setFilters);
    const setNewsStore = useNewsStore(s => s.setNews);
    const appendNewsStore = useNewsStore(s => s.appendNews);
    const resetFiltersStore = useNewsStore(s => s.resetFilters);
    const setLoadingStore = useNewsStore(s => s.setLoading);
    const hasActiveFilters = useNewsStore(s => s.hasActiveFilters);
    const resetStore = useNewsStore(s => s.resetStore);

    // Lấy danh sách categories từ useMasterDataStore
    const categories = useMasterDataStore(s => s.newsCategories);
    const fetchNewsCategories = useMasterDataStore(s => s.fetchNewsCategories);

    // Lấy danh sách chuyên khoa từ useMasterDataStore
    const departments = useMasterDataStore(s => s.departments);
    const fetchDepartments = useMasterDataStore(s => s.fetchDepartments);

    // Lấy ID của category ưu đãi
    const promotionCategory = categories.find(cat => cat.news_category_name === 'Ưu đãi');
    const promotionCategoryId = promotionCategory?.news_category_id;

    // Lấy danh sách tin tức
    const fetchNews = useCallback(
        (page = 1, isLoadMore = false) => {
            setLoadingStore(true);
            const newsService = new NewsService();
            const currentFilters = useNewsStore.getState().filters;
            newsService
                .getNewsList({
                    locale,
                    pageSize: 10,
                    pageIndex: page,
                    keyword: currentFilters.keyword,
                    news_category_id: promotionCategoryId || currentFilters.categoryId,
                    department_id: currentFilters.departmentId,
                })
                .then(res => {
                    const items = res.data || [];
                    const total = parseInt(res.total) || 0;

                    if (isLoadMore) {
                        appendNewsStore(items, total, page, 10);
                    } else {
                        setNewsStore(items, total, page, 10);
                    }
                })
                .catch(err => {
                    console.error(err);
                    setLoadingStore(false);
                });
        },
        [setLoadingStore, setNewsStore, appendNewsStore, locale, promotionCategoryId],
    );

    // Lấy danh sách categories từ useMasterDataStore
    const fetchCategories = useCallback(() => {
        fetchNewsCategories('vn');
    }, [fetchNewsCategories, locale]);

    // Xử lý tìm kiếm
    const handleSearchChange = useCallback(
        value => {
            setFiltersStore({keyword: value});

            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            if (value.length >= 4 || value.length === 0) {
                searchTimeoutRef.current = setTimeout(() => {
                    setLoadingStore(true);
                    const newsService = new NewsService();
                    const currentFilters = useNewsStore.getState().filters;

                    newsService
                        .getNewsList({
                            locale,
                            pageSize: 10,
                            pageIndex: 1,
                            keyword: value,
                            news_category_id: promotionCategoryId,
                            department_id: currentFilters.departmentId,
                        })
                        .then(res => {
                            const items = res.data || [];
                            const total = parseInt(res.total) || 0;
                            setNewsStore(items, total, 1, 10);
                        })
                        .catch(err => {
                            console.error(err);
                            setLoadingStore(false);
                        });
                }, 500);
            }
        },
        [setFiltersStore, setLoadingStore, setNewsStore, locale, promotionCategoryId],
    );

    // Xử lý tìm kiếm khi nhấn Enter
    const handleEnterSearch = useCallback(
        value => {
            if (value.length) {
                setFiltersStore({keyword: value});

                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                setLoadingStore(true);
                const newsService = new NewsService();
                const currentFilters = useNewsStore.getState().filters;

                newsService
                    .getNewsList({
                        locale,
                        pageSize: 10,
                        pageIndex: 1,
                        keyword: value,
                        news_category_id: promotionCategoryId,
                        department_id: currentFilters.departmentId,
                    })
                    .then(res => {
                        const items = res.data || [];
                        const total = parseInt(res.total) || 0;
                        setNewsStore(items, total, 1, 10);
                    })
                    .catch(err => {
                        console.error(err);
                        setLoadingStore(false);
                    });
            }
        },
        [setFiltersStore, setLoadingStore, setNewsStore, locale, promotionCategoryId],
    );

    // Xử lý thay đổi chuyên khoa
    const handleDepartmentChange = useCallback(
        value => {
            setFiltersStore({departmentId: value || ''});
            setLoadingStore(true);
            const newsService = new NewsService();
            const currentFilters = useNewsStore.getState().filters;

            newsService
                .getNewsList({
                    locale,
                    pageSize: 10,
                    pageIndex: 1,
                    keyword: currentFilters.keyword,
                    news_category_id: promotionCategoryId,
                    department_id: value || '',
                })
                .then(res => {
                    const items = res.data || [];
                    const total = parseInt(res.total) || 0;
                    setNewsStore(items, total, 1, 10);
                })
                .catch(err => {
                    console.error(err);
                    setLoadingStore(false);
                });
        },
        [setFiltersStore, setLoadingStore, setNewsStore, locale, promotionCategoryId],
    );

    // Xử lý xóa tất cả bộ lọc
    const handleClearFilters = useCallback(() => {
        resetFiltersStore();
        setLoadingStore(true);
        const newsService = new NewsService();

        newsService
            .getNewsList({
                locale,
                pageSize: 10,
                pageIndex: 1,
                keyword: '',
                news_category_id: promotionCategoryId,
                department_id: '',
            })
            .then(res => {
                const items = res.data || [];
                const total = parseInt(res.total) || 0;
                setNewsStore(items, total, 1, 10);
            })
            .catch(err => {
                console.error(err);
                setLoadingStore(false);
            });
    }, [resetFiltersStore, setLoadingStore, setNewsStore, locale, promotionCategoryId]);

    useEffect(() => {
        if (!locale) return;
        fetchCategories();
        fetchDepartments(locale); // Fetch danh sách chuyên khoa
    }, [locale, fetchCategories, fetchDepartments]);

    useEffect(() => {
        if (!locale) return;
        // Reset store và load data mới mỗi khi component mount
        resetStore();
        setFiltersStore({locale});
        fetchNews(1, false);
    }, [locale, setFiltersStore, fetchNews, resetStore]);

    // Reset store khi component unmount
    useEffect(() => {
        return () => {
            resetStore();
        };
    }, [resetStore]);

    const handleLoadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            fetchNews(pageIndex + 1, true);
        }
    }, [hasMore, isLoading, pageIndex, fetchNews]);

    // Hiệu ứng cuộn vô hạn
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop >=
                document.documentElement.offsetHeight - 1000
            ) {
                handleLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleLoadMore]);

    return (
        <section className="news-section">
            <div className="news-header">
                <h2>{getLocaleContent(locale, 'page.promotion-page.title')}</h2>
                <div className="line">
                    <div className="news-filters-wrapper">
                        <div className="news-filters">
                            {hasActiveFilters() && (
                                <div className="filter-clear">
                                    {/* <FilterButton onClick={handleClearFilters}>
                                        {getLocaleContent(locale, 'page.promotion-page.clear-filter')}
                                    </FilterButton> */}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="news-search-container">
                        <div className="news-department-select">
                            <FilterSelect
                                placeholder={getLocaleContent(locale, 'page.promotion-page.choose-department')}
                                value={filters.departmentId || ''}
                                onChange={handleDepartmentChange}
                                options={[
                                    {value: '', label: getLocaleContent(locale, 'page.promotion-page.all-departments')},
                                    ...departments.map(dept => ({
                                        value: dept.department_id,
                                        label: dept.department_name,
                                    })),
                                ]}
                            />
                        </div>
                        <div className="news-search">
                            <FilterInput
                                placeholder={getLocaleContent(locale, 'page.promotion-page.search-placeholder')}
                                value={filters.keyword}
                                onChange={e => handleSearchChange(e.target.value)}
                                onEnter={handleEnterSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="news-grid">
                {news.length === 0 && !isLoading && (
                    <div>{getLocaleContent(locale, 'page.promotion-page.no-promotions')}</div>
                )}
                {news.map((newsItem, index) => {
                    return (
                        <LinkPromotionDetail key={newsItem.news_id || index} news={newsItem}>
                            <div className="news-card">
                                <div className="news-image">
                                    <img
                                        src={newsItem.image_url || '/bw_image/news-image.png'}
                                        alt={newsItem.news_title}
                                    />
                                </div>
                                <div className="news-body">
                                    <div className="news-meta">
                                        <span className="category">{newsItem.department_name}</span>
                                        <span className="date">{newsItem.news_date}</span>
                                    </div>
                                    <h3 className="news-title">{newsItem.news_title}</h3>
                                    <p className="news-description">{newsItem.short_description}</p>
                                    <div className="news-red-more">
                                        <span className="read-more">
                                            {getLocaleContent(locale, 'page.promotion-page.view-detail')} →
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </LinkPromotionDetail>
                    );
                })}
            </div>

            {isLoading && <LoadingNavigation size="large" color="#006940" style={{marginBottom: '40px'}} />}
        </section>
    );
}
